import { FC, useEffect, useMemo, useState } from "react"
import { Column, Row, useTable } from "react-table"
import Dropdown, { DropdownOption } from "../../common/Dropdown"
import { StyledTable, StyledNoDataFound, StyledPagination } from "../../leaderboads/Styles"
import { PlayerStats } from "../../../models/leagueStats"
import LeagueStatsService from "../../../services/leagueStats"
import { QueryParams, Meta } from "../../../models/misc"
import { useUser } from "../../../context/useUser"
import HelperService from "../../../services/helper"
import useNotyf from "../../../context/useNotify"
import placeholderImage from "../../../images/common/vgc-logo.png"
import ReactPaginate from "react-paginate"
import LoadingSpinner from "../../common/LoadingSpinner"

export interface TeamData {
  _id: string
  name: string
}

interface Props {
  leagueId: string
  teams: TeamData[]
}

const LeagueStatsTable: FC<Props> = ({ leagueId, teams }) => {
  const notyf = useNotyf()

  // Data
  const { user } = useUser()

  const [playerStats, setPlayerStats] = useState<PlayerStats[]>([])
  const [displayStat, setDisplayStat] = useState<string>("goals")

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true)

  const [selectedStat, setSelectedStat] = useState<string>("goals")
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(undefined)

  const statOptions = useMemo(() => {
    return [
      { label: "Top Goals", value: "goals" },
      { label: "Top Assists", value: "assists" },
      { label: "Top Passes", value: "passes" },
      { label: "Top Tackles", value: "tackles" },
      { label: "Top Saves", value: "saves" },
      { label: "Top Clean Sheets", value: "cleanSheets" },
      { label: "Top Passes Attempted", value: "passesAttempted" },
      { label: "Top Passes Completed", value: "passesCompleted" },
      { label: "Top Tackles Attempted", value: "tacklesAttempted" },
      { label: "Top Tackles Completed", value: "tacklesCompleted" },
      { label: "Top Match Rating", value: "matchRating" },
      { label: "Top Shots", value: "shots" },
    ]
  }, [])

  const teamOptions = useMemo(() => {
    if (!teams) {
      return []
    }
    return teams.map((team) => {
      return { label: team.name, value: team._id }
    })
  }, [teams])

  const [meta, setMeta] = useState<Meta>({
    totalPages: 0,
    page: 1,
    pageSize: 10,
  })

  // Api
  const fetchPlayerStats = async (page: number = 1, stat: string = displayStat, teamId?: string) => {
    setIsLoading(true)
    try {
      if (!leagueId) {
        return
      }
      const filters: QueryParams = {
        page: page,
        pageSize: meta.pageSize,
        stat: stat,
      }
      if (teamId) {
        filters.teamId = teamId;
      }
      const data = await LeagueStatsService.listPlayerLeagueStats(leagueId, filters)
      setPlayerStats(data.data)
      setMeta(data.meta)
    } catch (err) {
      HelperService.showNotfyErr(notyf, err, "Error fetching league stats.")
    } finally {
      setIsLoading(false)
      setIsPageLoading(false)
    }
  }

  // Methods
  const handleSelectStat = (selectedOption: any) => {
    if (!selectedOption) {
      return
    }
    setSelectedStat(selectedOption.value)
    fetchPlayerStats(1, selectedOption.value, selectedTeamId)
  }

  const handleTeamSelect = (selectedOption: DropdownOption | null) => {
    if (selectedOption) {
      // Fetch player stats for the selected team
      setSelectedTeamId(selectedOption.value)
      fetchPlayerStats(1, selectedStat, selectedOption.value)
    } else {
      // Fetch player stats for all teams
      setSelectedTeamId(undefined)
      fetchPlayerStats(1, selectedStat)
    }
  }

  // Table Data
  const columns: Column<PlayerStats>[] = useMemo(
    () => [
      {
        Header: "Player",
        accessor: "team",
        Cell: ({ row }) => (
          <div className='d-flex align-items-center'>
            <img
              src={row.original?.team?.image || placeholderImage}
              alt={row.original?.team?.name}
              className='img-fluid me-2'
              style={{ maxWidth: "30px", maxHeight: "30px", borderRadius: "50%" }}
            />
            {row.original?.playerName || "N/A"}
          </div>
        ),
      },
      { Header: "G", accessor: "goals" },
      { Header: "A", accessor: "assists" },
      { Header: "P", accessor: "passes" },
      { Header: "T", accessor: "tackles" },
      { Header: "S", accessor: "saves" },
      { Header: "CS", accessor: "cleanSheets" },
      { Header: "PA", accessor: "passesAttempted" },
      { Header: "PC", accessor: "passesCompleted" },
      { Header: "TA", accessor: "tacklesAttempted" },
      { Header: "TC", accessor: "tacklesCompleted" },
      { Header: "MR", accessor: "matchRating" },
      { Header: "SH", accessor: "shots" },
    ],
    [playerStats]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: playerStats,
  })

  const getCellStyle = (row: Row<PlayerStats>, column: Column<PlayerStats>) => {
    if (row.original.playerName === user?.username) {
      return {
        color: "gold",
      }
    }
    return {}
  }

  // Hooks
  useEffect(() => {
    fetchPlayerStats(1, selectedStat, selectedTeamId)
  }, [leagueId])

  return (
    <>
      {/* Dropdowns */}
      <div>
        <div className='row justify-content-end'>
          <div className='col-lg-4 col-md-6 col-sm-8 mb-2'>
            <Dropdown options={teamOptions} onSelect={handleTeamSelect} text='Select Team' clearable={true} />
          </div>
          <div className='col-lg-4 col-md-6 col-sm-8 mb-2'>
            <Dropdown options={statOptions} onSelect={handleSelectStat} text='Top Goals' />
          </div>
        </div>
      </div>

      {/* Table */}
      {isPageLoading ? (
        <div className='d-flex justify-content-center align-items-center'>
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className='d-flex flex-column mb-3 mt-2'>
            <div className='table-responsive gray-border'>
              <StyledTable {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row)
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          return (
                            <td {...cell.getCellProps()} style={getCellStyle(row, cell.column)}>
                              {cell.render("Cell")}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </StyledTable>
              {rows.length === 0 && (
                <StyledNoDataFound>No player stats found. Check back later or adjust your search.</StyledNoDataFound>
              )}
            </div>
          </div>
          <div className='d-flex justify-content-end'>
            {playerStats.length ? (
              <StyledPagination>
                <ReactPaginate
                  previousLabel={"Previous"}
                  nextLabel={"Next"}
                  pageCount={meta.totalPages}
                  onPageChange={({ selected }) => {
                    if (isLoading) {
                      return false
                    }
                    fetchPlayerStats(selected + 1, selectedStat, selectedTeamId)
                  }}
                  forcePage={meta.page - 1}
                  containerClassName={"pagination"}
                  pageClassName={"page-item"}
                  pageLinkClassName={"page-link"}
                  previousClassName={"page-item"}
                  nextClassName={"page-item"}
                  previousLinkClassName={"page-link"}
                  nextLinkClassName={"page-link"}
                  activeClassName={"active"}
                />
              </StyledPagination>
            ) : null}
          </div>
        </>
      )}
    </>
  )
}

export default LeagueStatsTable